import React, { useEffect, useState } from "react";
import { Table, Button, Row, Col,  Tooltip, Drawer, Grid } from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchUnitsList, fetchBaseUnitsList, deleteUnit } from "../unitsThunks";
import { App } from "antd";
import { selectUnitsList } from "../unitsSelectors";
import { setSelectedUnit, setIsEditUnit } from "../unitsSlice";
import { getAccessType } from "../../../lib/functions";
import { useIsAdminUser } from "../../../lib/accessList";

const UnitsList = () => {
  const { modal } = App.useApp();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  let [access_type, access_type_set] = useState("");
  const isAdminUser = useIsAdminUser();
  const authInfo = useSelector((state) => state.auth?.auth_info);

  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;

  useEffect(() => {
    const access = getAccessType("item_unit");
    access_type_set(access);
  }, []);

  const { selectedUnit } = useSelector((state) => state.units);
  const [helpOpen, setHelpOpen] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };
  const handleDelete = async (unit_id) => {
    try {
      await dispatch(deleteUnit(unit_id)).unwrap();

      modal.success({
        title: "Unit deleted successfully",
      });
      dispatch(fetchUnitsList());
      dispatch(setSelectedUnit(null));
      dispatch(setIsEditUnit(false));
    } catch (error) {
      modal.warning({
        title: error.message || "Delete failed",
      });
    }
  };

  const unitsList = useSelector(selectUnitsList);

  const columns = [
    {
      title: "NO.",
      render: (_, __, index) => index + 1,
    },
    {
      title: "Unit Name",
      dataIndex: "unit_name",
      sorter: (a, b) => a.unit_name?.localeCompare(b.unit_name),
    },
    {
      title: "Master Unit",
      dataIndex: "unit_symbol",
      sorter: (a, b) => a.unit_symbol?.localeCompare(b.unit_symbol),
    },
    {
      title: "Multiple Unit",
      dataIndex: "is_multi_unit",
      sorter: (a, b) => a.is_multi_unit?.localeCompare(b.is_multi_unit),
    },
    {
      title: "Conversion",
      dataIndex: "conversion",
      sorter: (a, b) => a.conversion - b.conversion,
    },
    {
      title: "Base Unit Name",
      dataIndex: "base_unit_name",
      sorter: (a, b) => a.base_unit_name?.localeCompare(b.base_unit_name),
    },
    ...(access_type === "WRITE"
      ? [
        {
          title: "Actions",
          render: (_, record) => (
            <>
              <Button
                type="link"
                onClick={() => {
                  dispatch(setSelectedUnit(record));
                  dispatch(setIsEditUnit(true));
                  navigate("addNewUnit");
                }}
                icon={<EditOutlined />}
                disabled={!(isAdminUser || canEdit)}
              />
              <Button
                type="link"
                danger
                disabled={!(isAdminUser || canDelete)}
                onClick={() => {
                  modal.confirm({
                    title: "Are you sure?",
                    content: `This will delete unit: ${record.unit_name}`,
                    okText: "Yes, Delete",
                    cancelText: "Cancel",
                    onOk: () => {
                      dispatch(setSelectedUnit(record));
                      handleDelete(record.unit_id);
                    },
                  });
                }}
                icon={<DeleteOutlined />}
              />
            </>
          ),
        },
      ]
      : []),
  ];

  useEffect(() => {
    dispatch(setSelectedUnit(null));
    dispatch(setIsEditUnit(false));
    dispatch(fetchUnitsList());
    dispatch(fetchBaseUnitsList());
  }, [dispatch]);

  return (
    <>
      <Row justify="end" style={{ marginBottom: "32px", marginTop: "32px" }}>
        <Col>
          <Tooltip title="Help">
            <Button
              type="text"
              icon={<QuestionCircleOutlined style={{ fontSize: 18 }} />}
              onClick={() => setHelpOpen(true)}
            />
          </Tooltip>
        </Col>
        <Col>
          {access_type == "WRITE" && (
            <>
              <Button
                type="primary"
                className="traic-button"
                icon={<PlusOutlined />}
                style={{
                  minWidth: "112px",
                  height: "36px",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
                onClick={() => {
                  dispatch(setSelectedUnit(null));
                  dispatch(setIsEditUnit(false));
                  navigate("addNewUnit");
                }}
              >
                Add New
              </Button>
            </>
          )}
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={unitsList}
        rowKey="unit_id"
        pagination={{
          ...pagination,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50"],
        }}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
      />
      <Drawer
        title="Unit Management Help"
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        width={420}
        styles={{
          wrapper: {
            paddingTop: !screens.md ? "80px" : undefined
          }
        }}
      >
        <div style={{ lineHeight: 1.8 }}>
          <h2
            style={{
              fontSize: "18px",
              fontWeight: 600,
              marginBottom: "12px",
              marginLeft: "8px",
            }}
          >
            Unit Management
          </h2>

          <p
            style={{
              color: "#555",
              marginBottom: "16px",
              marginLeft: "8px",
            }}
          >
            The Unit Management module is used to create and manage inventory
            measurement units within the ERP system. Units help maintain
            accurate quantity handling, stock conversion, and inventory tracking.
          </p>

          <div
            style={{
              background: "#f5f5f5",
              padding: "14px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <strong>Available Actions</strong>

            <ol style={{ paddingLeft: "20px", marginTop: "10px" }}>
              <li>View existing inventory units</li>
              <li>Use <strong>Add New</strong> to create units</li>
              <li>Manage master and base unit relationships</li>
              <li>Configure conversion values between units</li>
              <li>Edit unit details when required</li>
              <li>Delete unused units if permitted</li>
              <li>Maintain inventory quantity conversion accuracy</li>
            </ol>
          </div>

          <div
            style={{
              background: "#fafafa",
              border: "1px solid #eee",
              padding: "14px",
              borderRadius: "8px",
            }}
          >
            <strong>Important Notes</strong>

            <ul
              style={{
                paddingLeft: "18px",
                marginTop: "10px",
                color: "#666",
              }}
            >
              <li>Conversion values directly affect inventory calculations.</li>
              <li>Base units should be configured carefully.</li>
              <li>Access permissions control edit and delete actions.</li>
              <li>Unit management improves inventory consistency and reporting.</li>
            </ul>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default UnitsList;
